import type { DictionaryBlocklistEntry } from "../api/metadataSpellcheckApi";

export interface BlocklistPhraseEntry {
  phrase: string;
  caseSensitive: boolean;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function findFirstBlocklistPhraseInTitle(
  title: string,
  entries: BlocklistPhraseEntry[]
): string | null {
  for (const { phrase, caseSensitive } of entries) {
    if (!phrase) continue;
    const flags = caseSensitive ? "u" : "iu";
    const pattern = new RegExp(`\\b${escapeRegex(phrase)}\\b`, flags);
    if (pattern.test(title)) return phrase;
  }
  return null;
}

export function blocklistEntriesToPhraseEntries(
  blocklist: DictionaryBlocklistEntry[]
): BlocklistPhraseEntry[] {
  return blocklist.map((e) => ({
    phrase: e.phrase,
    caseSensitive: e.caseSensitive,
  }));
}

export function resolveNoSubstituteHitsFromBlocklist(
  originalTitle: string,
  blocklist: DictionaryBlocklistEntry[]
): BlocklistPhraseEntry[] {
  const hits: BlocklistPhraseEntry[] = [];
  for (const entry of blocklist) {
    if (entry.substitute !== null) continue;
    const flags = entry.caseSensitive ? "u" : "iu";
    const pattern = new RegExp(`\\b${escapeRegex(entry.phrase)}\\b`, flags);
    if (pattern.test(originalTitle)) {
      hits.push({ phrase: entry.phrase, caseSensitive: entry.caseSensitive });
    }
  }
  return hits;
}

export function preferLongestBlocklistPhraseHits(
  hits: BlocklistPhraseEntry[]
): BlocklistPhraseEntry[] {
  const sorted = [...hits].sort((a, b) => b.phrase.length - a.phrase.length);
  const kept: BlocklistPhraseEntry[] = [];
  for (const hit of sorted) {
    const redundant = kept.some((k) => {
      if (k.phrase.length <= hit.phrase.length) return false;
      const flags = hit.caseSensitive ? "u" : "iu";
      return new RegExp(`\\b${escapeRegex(hit.phrase)}\\b`, flags).test(k.phrase);
    });
    if (!redundant) kept.push(hit);
  }
  return kept;
}

/** Phrases with substitutes that appear in original but not in corrected (auto-replaced). */
export function resolveSubstitutedHitsFromBlocklist(
  originalTitle: string,
  correctedTitle: string,
  blocklist: DictionaryBlocklistEntry[]
): BlocklistPhraseEntry[] {
  const raw: BlocklistPhraseEntry[] = [];
  for (const entry of blocklist) {
    if (entry.substitute === null) continue;
    const flags = entry.caseSensitive ? "u" : "iu";
    const pattern = new RegExp(`\\b${escapeRegex(entry.phrase)}\\b`, flags);
    if (!pattern.test(originalTitle)) continue;
    if (pattern.test(correctedTitle)) continue;
    raw.push({ phrase: entry.phrase, caseSensitive: entry.caseSensitive });
  }
  return preferLongestBlocklistPhraseHits(raw);
}
